import { addDays, setHours, setMinutes, startOfDay, parse } from 'date-fns';
import { isSlotBookable } from './slotValidation';

/**
 * Find the first available booking slot based on calendar configuration
 * @param {Object} calendarConfig - Calendar configuration object
 * @param {number} daysToCheck - Maximum number of days to check ahead (default: 60)
 * @returns {Date|null} First available date or null if none found
 */
export const findFirstAvailableSlot = (calendarConfig, daysToCheck = 60) => {
  if (!calendarConfig) {
    return new Date(); // Return today if no config
  }

  const today = startOfDay(new Date());
  const availabilityRules = calendarConfig.availability_rules;

  if (!availabilityRules) {
    return today;
  }

  const workingDays = availabilityRules.days || [];
  const hoursStart = availabilityRules.hours_start || '09:00';
  const granularity = parseInt(calendarConfig.general?.granularity) || 30;

  const dayMap = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  // Convert working days to numbers
  const workingDayNumbers = workingDays.map(day => dayMap[day.toLowerCase()]).filter(d => d !== undefined);

  if (workingDayNumbers.length === 0) {
    return today; // No working days configured
  }

  // Check each day up to daysToCheck
  for (let dayOffset = 0; dayOffset < daysToCheck; dayOffset++) {
    const checkDate = addDays(today, dayOffset);
    const dayOfWeek = checkDate.getDay();

    // Skip non-working days
    if (!workingDayNumbers.includes(dayOfWeek)) {
      continue;
    }

    // Parse start time
    const [startHour, startMinute] = hoursStart.split(':').map(Number);
    const firstSlotTime = setMinutes(setHours(checkDate, startHour), startMinute);

    // Check if this slot is bookable
    const { isBookable } = isSlotBookable(firstSlotTime, calendarConfig);
    
    if (isBookable) {
      return checkDate;
    }
  }

  // No available slots found, return today
  return today;
};
